import api from "./api";
import type {
  AgendaDayPayload,
  AgendaFiltersState,
  AgendaWeekSummary,
} from "../pages/Schedules/agendaTypes";
import {
  adaptLegacySchedulesToDayPayload,
  buildWeekSummaryFromLegacy,
  isAgendaEndpointUnavailable,
  LegacySchedulesPayload,
} from "../pages/Schedules/agendaLegacyAdapter";
import { normalizeAgendaIsoDate } from "../pages/Schedules/agendaFiltersQuery";
import { getFortalezaTodayIso } from "../pages/Schedules/agendaFiltersQuery";
import {
  buildAgendaWeekSummaryParams,
  projectWeekSummaryToWeekStrip,
} from "../pages/Schedules/agendaWeekDateRange";

function normalizeWeekSummaryDates(summary: AgendaWeekSummary): AgendaWeekSummary {
  return {
    days: summary.days.map((day) => ({
      ...day,
      date: normalizeAgendaIsoDate(day.date) ?? day.date,
    })),
  };
}

function unwrapLegacyPayload(response: {
  data?: { data?: LegacySchedulesPayload } | LegacySchedulesPayload;
}): LegacySchedulesPayload {
  const payload = response.data;
  if (payload && typeof payload === "object" && "data" in payload) {
    return (payload as { data: LegacySchedulesPayload }).data ?? {};
  }
  return (payload as LegacySchedulesPayload) ?? {};
}

async function fetchLegacySchedulesGrid(
  teamId?: number
): Promise<LegacySchedulesPayload> {
  const response =
    teamId != null
      ? await api.get("/api/adm/schedules/search-by-team", {
          params: { team_id: teamId },
        })
      : await api.get("/api/adm/schedules");

  return unwrapLegacyPayload(response);
}

function unwrapData<T>(response: { data?: { data?: T } | T }): T {
  const payload = response.data;
  if (payload && typeof payload === "object" && "data" in payload) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

export function buildAgendaDayParams(
  filters: AgendaFiltersState
): Record<string, string | number> {
  const params: Record<string, string | number> = {
    date: filters.date,
  };

  if (filters.teamId != null) {
    params.team_id = filters.teamId;
  }
  if (filters.city) {
    params.city = filters.city;
  }
  if (filters.visitType !== "all") {
    params.type = filters.visitType;
  }
  if (filters.consultantId != null) {
    params.consultant_id = filters.consultantId;
  }

  return params;
}

export async function fetchAgendaDay(
  filters: AgendaFiltersState
): Promise<AgendaDayPayload> {
  try {
    const response = await api.get("/api/adm/schedules/day", {
      params: buildAgendaDayParams(filters),
    });
    return unwrapData<AgendaDayPayload>(response);
  } catch (error) {
    if (!isAgendaEndpointUnavailable(error)) {
      throw error;
    }

    const legacy = await fetchLegacySchedulesGrid(filters.teamId);
    return adaptLegacySchedulesToDayPayload(legacy, filters);
  }
}

export async function fetchAgendaWeekSummary(
  filters: Pick<AgendaFiltersState, "teamId">
): Promise<AgendaWeekSummary> {
  const todayIso = getFortalezaTodayIso();
  const params = buildAgendaWeekSummaryParams(filters.teamId, todayIso);

  try {
    const response = await api.get("/api/adm/schedules/week-summary", {
      params,
    });
    const normalized = normalizeWeekSummaryDates(
      unwrapData<AgendaWeekSummary>(response)
    );
    return projectWeekSummaryToWeekStrip(normalized, todayIso);
  } catch (error) {
    if (!isAgendaEndpointUnavailable(error)) {
      throw error;
    }

    const legacy = await fetchLegacySchedulesGrid(filters.teamId);
    const schedules = Array.isArray(legacy.schedules) ? legacy.schedules : [];
    const legacySummary = normalizeWeekSummaryDates(
      buildWeekSummaryFromLegacy(legacy, todayIso, schedules)
    );
    return projectWeekSummaryToWeekStrip(legacySummary, todayIso);
  }
}
